import axios, { AxiosError } from 'axios';
import {
  CreateMortgageLeadTassoCommand,
  MortgageLeadTasso,
} from '@tassomutuo-backoffice/types';
import { axiosApiInstancePrivate } from '../index';

const BASE_PATH = '/v1/backoffice/mortgage-leads';

/**
 * The rejected shape every Lead Tasso caller reads: the backend's `{ code,
 * message }` envelope (`existingRequestId` only on the two `409` variants),
 * plus the HTTP status the envelope alone does not carry. A response with no
 * body (network failure, a gateway with no JSON) still reports its `status`,
 * which is what tells a `5xx` apart from a dropped connection.
 */
export interface MortgageLeadTassoErrorEnvelope {
  status: number | null;
  code?: string;
  message?: string;
  existingRequestId?: string | null;
}

function handleAxiosError(err: unknown): never {
  const error = err as Error | AxiosError;
  if (axios.isAxiosError(error)) {
    const data = (error.response?.data ?? {}) as Partial<MortgageLeadTassoErrorEnvelope>;
    // Every Lead Tasso caller reads this envelope's `code`/`message`/`existingRequestId` directly.
    // eslint-disable-next-line no-throw-literal
    throw {
      ...data,
      status: error.response?.status ?? null,
    } satisfies MortgageLeadTassoErrorEnvelope;
  }
  throw err;
}

/**
 * Registers a Lead Tasso acquired through an external channel (e.g. a call to
 * Euroansa's central office). The backend authenticates the actor, forces
 * central Programma Tasso routing and Origine di acquisizione, and forces both
 * consents to true; the command below carries only the customer and mortgage
 * data the operator collected.
 */
export const createMortgageLeadTasso = async (
  command: CreateMortgageLeadTassoCommand,
  idempotencyKey: string
): Promise<MortgageLeadTasso> => {
  try {
    const { data } = await axiosApiInstancePrivate.post<MortgageLeadTasso>(
      BASE_PATH,
      command,
      { headers: { 'Idempotency-Key': idempotencyKey } }
    );
    return data;
  } catch (err) {
    handleAxiosError(err);
  }
};
